// @flow
import {useStripe} from '@stripe/react-stripe-js';

import {message} from 'antd';
import lodash from 'lodash';
import React, {useCallback, useContext} from 'react';
import {v4 as uuidv4} from 'uuid';

import {useAuth} from '_contexts/auth';
import useAnalytics, {event} from '_hooks/useAnalytics';
import {checkPaymentMethodRequired} from '_utils/checkout';
import {getMe} from '_utils/profile';
import {createSubscription} from '_utils/stripe';

import {useCheckoutState} from './state';
import type {Dispatch} from './types';

export const CheckoutDispatchContext: React$Context<?Dispatch> = React.createContext<?Dispatch>();

type CheckoutFunctions = {
  selectPlan: (plan: Object) => void,
  resetPlan: () => void,
  applyPromotionCode: (promotionCode: Object) => void,
  resetPromotionCode: () => void,
  setPaymentMethod: (paymentMethod: Object) => void,
  resetPaymentMethod: () => void,
  purchase: () => Promise<void>,
};

export const useCheckout = (): CheckoutFunctions => {
  const dispatch = useContext(CheckoutDispatchContext);
  const stripe = useStripe();
  if (!dispatch) {
    throw new Error('useCheckout must be used within a CheckoutProvider');
  }

  const {track} = useAnalytics();

  const {selectedPlan, promotionCode, paymentMethod} = useCheckoutState();

  const selectPlan = useCallback(
    (plan: Object) =>
      dispatch({
        type: 'SET_PLAN',
        plan,
      }),
    [dispatch],
  );

  const resetPlan = useCallback(
    () =>
      dispatch({
        type: 'SET_PLAN',
        plan: null,
      }),
    [dispatch],
  );

  const applyPromotionCode = useCallback(
    (pc: Object) =>
      dispatch({
        type: 'APPLY_PROMOTION_CODE',
        promotionCode: pc,
      }),
    [dispatch],
  );

  const resetPromotionCode = useCallback(
    () =>
      dispatch({
        type: 'RESET_PROMOTION_CODE',
      }),
    [dispatch],
  );

  const setPaymentMethod = useCallback(
    (pm: Object) =>
      dispatch({
        type: 'SET_PAYMENT_METHOD',
        paymentMethod: pm,
      }),
    [dispatch],
  );

  const resetPaymentMethod = useCallback(
    () =>
      dispatch({
        type: 'SET_PAYMENT_METHOD',
        paymentMethod: null,
      }),
    [dispatch],
  );

  const purchase = useCallback(
    () =>
      (async () => {
        if (!selectedPlan) {
          throw new Error('tried to purchase nothing');
        }

        const paymentMethodRequired = checkPaymentMethodRequired(selectedPlan, promotionCode);
        if (paymentMethodRequired && !paymentMethod) {
          throw new Error('no valid payment method');
        }

        dispatch({
          type: 'SET_PURCHASING',
          purchasing: true,
        });

        const subscription = {
          priceId: selectedPlan.id,
          promotionCodeId: promotionCode?.id || null,
          paymentMethodId: paymentMethodRequired ? paymentMethod?.id : null,
          cancelAtPeriodEnd: !paymentMethodRequired,
          metadata: {
            promoId: promotionCode?.id || '',
            promoCode: promotionCode?.code || '',
            couponId: promotionCode?.coupon.id || '',
            value: promotionCode?.coupon.percentOff.toString() || '',
          },
        };

        try {
          const subscribeRes = await createSubscription(uuidv4(), subscription);

          if (subscribeRes.id && lodash.includes(['ACTIVE', 'TRIALING'], subscribeRes.status)) {
            dispatch({
              type: 'COMPLETE_PURCHASE',
            });
            if (subscribeRes.status === 'ACTIVE') {
              track({
                name: event.af.subscribe,
                data: {
                  af_revenue: subscribeRes.latestInvoice.amount_paid,
                  af_currency: subscribeRes.latestInvoice.currency,
                },
              });
            } else if (subscribeRes.status === 'TRIALING') {
              track({
                name: event.af.startTrial,
                data: {
                  af_price: selectedPlan.unitAmount / 100,
                  af_currency: subscribeRes.latestInvoice.currency,
                },
              });
            }
            return;
          }
          if (subscribeRes.id && subscribeRes.status === 'INCOMPLETE') {
            const result = await stripe.confirmCardPayment(subscribeRes.latestInvoice.paymentIntent.clientSecret, {
              payment_method: paymentMethod.id,
            });
            if (result.error) {
              throw new Error(result.error.message);
            }

            const currentUser = await getMe();
            if (currentUser.entitlement.status === 'ACTIVE') {
              dispatch({
                type: 'COMPLETE_PURCHASE',
              });
              track({
                name: event.af.subscribe,
                data: {
                  af_revenue: subscribeRes.latestInvoice.amount_paid,
                  af_currency: subscribeRes.latestInvoice.currency,
                },
              });
            }
            return;
          }
          throw new Error(`failed to purchase: status=${subscribeRes?.status}`);
        } catch (err) {
          message.error(err.message);
        } finally {
          dispatch({
            type: 'SET_PURCHASING',
            purchasing: false,
          });
        }
      })(),
    [dispatch, selectedPlan, promotionCode, paymentMethod, track, stripe],
  );

  return {
    selectPlan,
    resetPlan,
    applyPromotionCode,
    resetPromotionCode,
    setPaymentMethod,
    resetPaymentMethod,
    purchase,
  };
};
